import React, { useContext, useEffect, useRef } from 'react';
import { ConfigContext } from '../contexts/ConfigContext';

interface GtmSettings {
  gtmId: string;
  environment: string;
  auth?: string;
  preview?: string;
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Injects the Google Tag Manager container. This replaces react-gtm-module,
 * whose last release was 2020. The ids come from the runtime config rather
 * than the build, so the snippet cannot live in index.html.
 */
function injectTagManager({ gtmId, environment, auth, preview }: GtmSettings): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  window.dataLayer.push({ environment });

  const query = new URLSearchParams({ id: gtmId });
  if (auth) {
    query.set('gtm_auth', auth);
  }
  if (preview) {
    query.set('gtm_preview', preview);
    query.set('gtm_cookies_win', 'x');
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?${query.toString()}`;
  document.head.appendChild(script);
}

export default function GoogleTagManager({ children }: { children: React.JSX.Element }): React.JSX.Element {
  const { environment, gtmId, gtmAuth, gtmPreview } = useContext(ConfigContext);
  const injected = useRef(false);

  // Guarded with a ref because StrictMode runs effects twice in development,
  // and the container must only be added once.
  useEffect(() => {
    if (injected.current || !gtmId || environment === 'local') {
      return;
    }
    injected.current = true;
    injectTagManager({ gtmId, environment, auth: gtmAuth || undefined, preview: gtmPreview || undefined });
  }, [environment, gtmId, gtmAuth, gtmPreview]);

  return <>{children}</>;
}
